import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { FeaturedItemResult } from '@app/search/components/search-view';
import { DetailsModalService } from '@app/shared/services/content/details-modal.service';
import { WindowToken } from '@app/shared/window.token';
import { LDFlagsService, TrackerService } from '@dg/shared-services';
import SwiperCore, { A11y, EffectCoverflow, SwiperOptions } from 'swiper';
import { SearchOrigin, SearchTrackerService } from '@app/search';

// adding this here as the SlideData interface is not exported from Swiper.
interface SlideData {
  isPrev: boolean;
  isNext: boolean;
}

SwiperCore.use([EffectCoverflow, A11y]);

@Component({
  selector: 'dgx-search-featured-carousel',
  templateUrl: './search-featured-carousel.component.html',
  styleUrls: ['./search-featured-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFeaturedCarouselComponent {
  @Input() public items: FeaturedItemResult[];

  constructor(
    private detailsModalService: DetailsModalService,
    private ldFlagService: LDFlagsService,
    private searchTrackerService: SearchTrackerService,
    private trackerService: TrackerService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  public config: SwiperOptions = {
    slidesPerView: 2,
    spaceBetween: 0,
    centeredSlides: true,
    initialSlide: 1,
    slideToClickedSlide: true,
    loop: true,
    loopPreventsSlide: false,
    loopAdditionalSlides: 6,
    longSwipes: false,
    effect: 'coverflow',
    coverflowEffect: {
      rotate: 0,
      slideShadows: false,
      scale: 0.8,
    },
    shortSwipes: false,
    threshold: 500,
  };

  public get hasItems() {
    return this.items.length > 0;
  }

  public getBackgroundImage(
    carouselData: SlideData,
    backgroundImageUrl: string
  ): string {
    const url = `url(${backgroundImageUrl})`;
    const gradients = {
      previous:
        'linear-gradient(to right, rgba(1, 48, 89) 30%, rgba(1, 39, 70, 0.00))',
      next: 'linear-gradient(to left, rgba(1, 48, 89) 30%, rgba(1, 39, 70, 0.00))',
      current: 'linear-gradient(rgba(0, 0, 0, 0.60), rgba(0, 0, 0, 0.60))',
    };
    const { isPrev, isNext } = carouselData;
    let gradient = gradients.current;
    if (isPrev) {
      gradient = gradients.previous;
    } else if (isNext) {
      gradient = gradients.next;
    }

    return `${gradient}, ${url}`;
  }

  public openDetails(item: LearningResourceViewModel): void {
    const isAcademiesUser = this.ldFlagService.isPaidCMUser;
    if (isAcademiesUser && item.model.marketplaceInputId) {
      this.searchTrackerService.contentOpened(item, {
        location: 'Search Marketplace Featured Items',
        searchTerm: '',
        currentPage: 1,
        resultPosition: this.items.findIndex(
          (i) => i?.model?.model?.resourceId === item?.model?.resourceId
        ),
        totalContentResults: this.items.length,
      });
      const currentPath = window.location.href;
      // Replaces the empty srcpath with the current href.
      const resourceUrl = item.url.replace(
        /(\?|&)srcpath=.*?(&|$)/,
        `$1srcpath=${encodeURIComponent(currentPath)}$2`
      );
      this.windowRef.open(resourceUrl, '_self');
    } else {
      // temporarily set the location to indicate the featured items
      this.trackerService.setLocation(SearchOrigin.featuredItems);
      this.detailsModalService
        .openDetails(item, {
          isMarketplaceProgram: true,
        })
        .subscribe({
          // reset tracking location back to Search Marketplace
          complete: () =>
            this.trackerService.setLocation(SearchOrigin.marketplace),
        });
    }
  }

  public onSlideChange(event: any) {
    const { startX, startY, currentX, currentY } = event[0].touches;
    const item = this.items[event[0].realIndex] as FeaturedItemResult;
    const { resourceId, resourceType } = item.model;
    if ([startX, startY, currentX, currentY].every((num) => num > 0)) {
      // ignore the initial event that gets fired during init
      this.searchTrackerService.searchFeaturedCarouselItemClicked(
        resourceId,
        resourceType
      );
    }

    this.trackerService.trackEventData({
      action: 'Content Impressed',
      properties: {
        location: 'Search Marketplace Featured Items',
        inputType: resourceType,
        contentId: resourceId,
        title: item.model?.title || item.title,
        providerId: item.model?.model?.providerId,
        providerName: item.model?.model?.providerName,
        position: event[0].realIndex,
      },
    });
  }
}
